import { type ColDef, type GridApi, type GridReadyEvent, type ICellRendererParams, type RowClassParams, type ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { ExternalLink, Loader2, Printer, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link, useSearchParams } from "react-router";
import { apiPost, apiSend, useApi } from "../api";
import type { PortalConfig } from "../config";
import { confirmBox } from "../confirm";
import { messagePreview } from "../emailText";
import { date, dateTime, money, trackingUrl } from "../format";
import type { LeadList, LeadRow, LeadTab, LeadTag, Lookups } from "../types";
import { CustomerCard } from "./ticket/TopRow";
import { MessagesCard } from "./ticket/RightColumn";
import { TouchesCard } from "./ticket/MiddleColumn";
import { TicketContext, useTicket, useTicketContext } from "./ticket/useTicket";
import { theme } from "./TicketsPage";

/**
 * Leads (owner, 2026-11-04): tickets whose watch hasn't arrived. To do (everything waiting on staff, tagged with
 * why), Follow up (label sent, no package yet), Mailers (mailed labels and boxes), All leads. The selected lead
 * opens on the right: its next step, then Touches and Messages side by side.
 */
const TABS: { id: LeadTab; label: string; hint: string }[] = [
  { id: "todo", label: "To do", hint: "Every unread lead (including In Transit and Lead Archived tickets with an unread message) and every mailer waiting to be sent. A lead leaves when it's marked read and comes back if it becomes unread again." },
  { id: "follow", label: "Follow up", hint: "Customers with a label who haven't mailed their watch yet. Leads with no package 90 days after the label move to Lead Archived on their own." },
  { id: "mailers", label: "Mailers", hint: "Labels and boxes already mailed: on their way (Mailer Outgoing) or delivered and waiting for the watch (Mailer Arrived)." },
  { id: "verify", label: "Verify Address", hint: "Every ticket waiting for its address to be checked, so its label can be bought (including old Mail Labels tickets that never got a label)." },
  { id: "insurance", label: "Insurance", hint: "Every Insurance Selected ticket: shipping protection over the label limit, to confirm with the customer." },
  { id: "all", label: "All leads", hint: "Every lead whose watch hasn't arrived yet." },
];

const TAG_LABEL: Record<LeadTag, string> = {
  reply: "Reply", address: "Verify address", insurance: "Insurance", "mail-label": "Mail label", "mail-box": "Mail box",
};

const name = (r: LeadRow) => [r.firstName, r.lastName].filter(Boolean).join(" ") || "(no name)";
const tagText = (r: LeadRow) => r.tags.map((t) => TAG_LABEL[t]).join(", ");
const carrierFor = (n: string) => (/^1Z/i.test(n) ? "UPS" : "USPS");

function MessageCell(p: ICellRendererParams<LeadRow & { preview: string | null }>) {
  const r = p.data;
  if (!r?.preview) return <span className="muted">—</span>;
  return (
    <span className={`lead-msg${r.unread ? " unread" : ""}`}>
      <span className="text">{r.preview}</span>
      <span className="when">{r.lastMessageVia === "text" ? "Text" : "Email"} · {dateTime(r.lastMessageAt)}</span>
    </span>
  );
}

function TicketCell(p: ICellRendererParams<LeadRow>) {
  const r = p.data;
  if (!r) return null;
  return <span className="lead-no">{r.ticketNo}{r.unread && <span className="dot" title="Unread customer message" />}</span>;
}

export function LeadsPage({ config, lookups }: { config: PortalConfig; lookups: Lookups | null }) {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.id === params.get("tab"))?.id ?? "todo") as LeadTab;
  const [reload, setReload] = useState(0);
  const { data, error, loading } = useApi<LeadList>(config, "/lead-queue", reload);
  const [quick, setQuick] = useState("");
  const [selectedNo, setSelectedNo] = useState<number | null>(null);
  const [checked, setChecked] = useState<LeadRow[]>([]);
  const gridRef = useRef<GridApi<LeadRow> | null>(null);

  const rows = useMemo(() => (data?.rows ?? []).filter((r) => r.tabs.includes(tab))
    .map((r) => ({ ...r, preview: messagePreview(r.lastMessage), needs: tagText(r), name: name(r) })), [data, tab]);
  const selected = rows.find((r) => r.ticketNo === selectedNo) ?? data?.rows.find((r) => r.ticketNo === selectedNo) ?? null;
  // Pick the first lead when nothing is selected, or when another tab is opened. A lead that leaves this tab after
  // an action (marked read, mailer sent) stays open, so its notice and print-out link stay in view.
  useEffect(() => { if (rows.length && selectedNo == null) setSelectedNo(rows[0]!.ticketNo); }, [rows, selectedNo]);

  const columns = useMemo<ColDef<LeadRow>[]>(() => {
    const mailers = tab === "mailers";
    return [
      { field: "ticketNo", headerName: "Ticket", width: 105, cellRenderer: TicketCell, filter: "agNumberColumnFilter" },
      { colId: "name", headerName: "Customer", width: 160, valueGetter: (p) => p.data && name(p.data) },
      // Status replaces Needs (owner, 2026-11-04); unread shows as the dot by the ticket number.
      { field: "status", headerName: "Status", width: 170 },
      ...(mailers ? [
        { colId: "mailing", headerName: "Mailing", width: 115, valueGetter: (p) => p.data?.mailer ? (p.data.mailer.kind === "box" ? "Box + label" : "Label") : "" },
        { colId: "sent", headerName: "Sent", width: 120, filter: false, valueGetter: (p) => p.data?.mailer?.sentAt ?? "",
          valueFormatter: (p: ValueFormatterParams<LeadRow, string>) => date(p.value) },
        { colId: "tracking", headerName: "Tracking", width: 190, valueGetter: (p) => p.data?.mailer?.trackingNumber ?? "",
          cellRenderer: (p: ICellRendererParams<LeadRow, string>) => {
            const u = p.value ? trackingUrl(carrierFor(p.value), p.value) : null;
            return u ? <a href={u} target="_blank" rel="noreferrer">{p.value}</a> : (p.value || "—");
          } },
      ] as ColDef<LeadRow>[] : []),
      { field: "brands", headerName: "Brands", width: 140 },
      { field: "insured", headerName: "Insured", width: 105, filter: "agNumberColumnFilter", type: "rightAligned",
        valueFormatter: (p: ValueFormatterParams<LeadRow, number>) => (p.value ? money(p.value) : "—") },
      { field: "days", headerName: "Days", width: 90, filter: "agNumberColumnFilter", type: "rightAligned", sort: "desc",
        headerTooltip: "Days since the label (or since the ticket was made, if there is no label)",
        cellClass: (p) => (Number(p.value) >= 30 ? "days-late" : Number(p.value) >= 7 ? "days-warn" : "") },
      { field: "touches", headerName: "Touches", width: 100, filter: "agNumberColumnFilter", type: "rightAligned" },
      { colId: "message", headerName: "Latest customer message", flex: 1, minWidth: 260, cellRenderer: MessageCell,
        valueGetter: (p) => (p.data as LeadRow & { preview?: string | null } | undefined)?.preview ?? "",
        tooltipValueGetter: (p) => (p.data as LeadRow & { preview?: string | null } | undefined)?.preview ?? undefined },
      { field: "email", headerName: "Email", width: 200, hide: true },
      { field: "phone", headerName: "Phone", width: 130, hide: true },
      { colId: "place", headerName: "City", width: 150, hide: true, valueGetter: (p) => [p.data?.city, p.data?.state].filter(Boolean).join(", ") },
    ];
  }, [tab]);

  const defaultColDef = useMemo<ColDef<LeadRow>>(() => ({ sortable: true, filter: "agTextColumnFilter", resizable: true }), []);
  const onGridReady = useCallback((e: GridReadyEvent<LeadRow>) => { gridRef.current = e.api; }, []);
  useEffect(() => { gridRef.current?.setGridOption("quickFilterText", quick); }, [quick]);
  const rowClass = useCallback((p: RowClassParams<LeadRow>) => (p.data?.ticketNo === selectedNo ? "lead-current" : ""), [selectedNo]);
  useEffect(() => { gridRef.current?.redrawRows(); }, [selectedNo]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  return (
    <div className="grid-page leads-page">
      <div className="leads-head">
        <h2>Leads</h2>
        <nav className="lead-tabs" aria-label="Lead lists">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={t.id === tab ? "on" : ""} aria-pressed={t.id === tab}
              onClick={() => { const n = new URLSearchParams(params); n.set("tab", t.id); setParams(n); setChecked([]); setSelectedNo(null); }}>
              {t.label} <span className={`count${t.id === "todo" && (data?.counts.todo ?? 0) > 0 ? " hot" : ""}`}>{data ? data.counts[t.id].toLocaleString() : "…"}</span>
            </button>
          ))}
        </nav>
        <input type="search" value={quick} onChange={(e) => setQuick(e.target.value)} placeholder="Filter: name, email, phone, ticket #, brand…" aria-label="Filter leads" />
        <button type="button" className="tb-btn" onClick={refresh} title="Refresh the list"><RefreshCw size={15} /> {loading ? "Updating…" : "Refresh"}</button>
      </div>
      <p className="hint lead-hint">{TABS.find((t) => t.id === tab)!.hint}</p>
      {error && <p className="error">{error.message}</p>}

      {checked.length > 0 && <LeadBulk config={config} lookups={lookups} rows={checked} onDone={() => { refresh(); gridRef.current?.deselectAll(); }}
        onClear={() => gridRef.current?.deselectAll()} />}

      <div className="leads-split">
        <div className="grid-box leads-grid">
          <AgGridReact<LeadRow>
            theme={theme}
            rowData={data ? rows : null}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            getRowId={(p) => p.data.ticketId}
            rowSelection={{ mode: "multiRow", checkboxes: true, headerCheckbox: true, enableClickSelection: false, selectAll: "filtered" }}
            onSelectionChanged={(e) => setChecked(e.api.getSelectedRows())}
            onRowClicked={(e) => e.data && setSelectedNo(e.data.ticketNo)}
            getRowClass={rowClass}
            onGridReady={onGridReady}
            pagination
            paginationPageSize={50}
            paginationPageSizeSelector={[25, 50, 100, 250]}
            tooltipShowDelay={400}
            overlayNoRowsTemplate="Nothing here right now."
            loading={!data && !error}
          />
        </div>
        <aside className="lead-ws" aria-label="Selected lead">
          {selected ? <LeadWorkspace key={selected.ticketNo} config={config} lookups={lookups} row={selected} onChanged={refresh} />
            : <p className="muted">Pick a lead from the list.</p>}
        </aside>
      </div>
    </div>
  );
}

/** Selected leads: status, read/unread, resend the label link, archive. */
function LeadBulk({ config, lookups, rows, onDone, onClear }: { config: PortalConfig; lookups: Lookups | null; rows: LeadRow[]; onDone: () => void; onClear: () => void }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [statusId, setStatusId] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const ticketNos = rows.map((r) => r.ticketNo);
  const n = rows.length;
  const s = (k: number) => `${k} lead${k === 1 ? "" : "s"}`;
  const run = async (fn: () => Promise<string>) => {
    setBusy(true); setNote(null);
    try { setNote({ ok: true, text: await fn() }); onDone(); } catch (e) { setNote({ ok: false, text: (e as Error).message }); } finally { setBusy(false); }
  };
  const archivedId = lookups?.statuses.find((x) => x.systemKey === "lead_archived")?.id;
  const statuses = lookups?.statuses.filter((x) => x.isActive) ?? [];
  return (
    <div className="bulk-bar lead-bulk" role="region" aria-label="Selected leads">
      <strong>{s(n)} selected</strong>
      <select value={statusId} onChange={(e) => setStatusId(e.target.value)} aria-label="New status">
        <option value="">Change status…</option>
        {statuses.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
      </select>
      <button type="button" disabled={!statusId || busy} onClick={async () => {
        const st = statuses.find((x) => String(x.id) === statusId);
        if (!st || !(await confirmBox(`Change ${s(n)} to "${st.name}"?\n\nNo email or text is sent.`, { title: "Change status", ok: "Change status" }))) return;
        void run(async () => { const r = await apiPost<{ changed: number }>(config, token, "/tickets/actions/status", { ticketNos, statusId: st.id }); setStatusId(""); return `${s(r.changed)} moved to ${st.name}.`; });
      }}>Apply</button>
      <button type="button" disabled={busy} onClick={() => void run(async () => { await apiPost(config, token, "/tickets/actions/mark-read", { ticketNos }); return `Marked ${s(n)} read.`; })}>Mark read</button>
      <button type="button" disabled={busy} onClick={async () => {
        if (!(await confirmBox(`Email (and text, where allowed) the label link to ${s(n)}?`, { title: "Resend label link", ok: "Resend" }))) return;
        void run(async () => {
          let ok = 0; const failed: number[] = [];
          for (const no of ticketNos) {
            try { await apiSend(config, token, "POST", `/tickets/${no}/resend-label`, {}); ok++; } catch { failed.push(no); }
          }
          return `Label link resent to ${s(ok)}.${failed.length ? ` Not sent: #${failed.join(", #")}.` : ""}`;
        });
      }}>Resend label link</button>
      <button type="button" className="danger" disabled={busy || !archivedId} onClick={async () => {
        if (!(await confirmBox(`Move ${s(n)} to Lead Archived?`, { title: "Archive", ok: "Archive", danger: true }))) return;
        void run(async () => { const r = await apiPost<{ changed: number }>(config, token, "/tickets/actions/status", { ticketNos, statusId: archivedId }); return `${s(r.changed)} archived.`; });
      }}>Archive</button>
      <button type="button" className="link" onClick={onClear}>Clear selection</button>
      {note && <span className={note.ok ? "ok" : "error"}>{note.text}</span>}
    </div>
  );
}

/** The selected lead: what to do next, the customer, then Touches and Messages side by side. */
function LeadWorkspace({ config, lookups, row, onChanged }: { config: PortalConfig; lookups: Lookups | null; row: LeadRow; onChanged: () => void }) {
  const { detail, loadError, problem, setProblem, notice, setNotice, saving, save, reload } = useTicket(config, String(row.ticketNo));
  // Any change made here refreshes the list (tags, counts) once the ticket has been saved.
  const first = useRef(true);
  useEffect(() => { if (!detail) return; if (first.current) { first.current = false; return; } onChanged(); }, [detail, onChanged]);
  if (loadError) return <p className="error">{loadError}</p>;
  if (!detail) return <p className="muted">Loading #{row.ticketNo}…</p>;
  return (
    <TicketContext.Provider value={{ config, lookups, detail, save, saving, reload, notify: setNotice }}>
      <div className="tp lead-tp">
        <div className="lead-ws-head">
          <h3>#{row.ticketNo} · {name(row)}</h3>
          <span className="lead-pill plain">{detail.ticket.status}</span>
          <span className="spacer" />
          <Link className="btn outline" to={`/tickets/${row.ticketNo}`}><ExternalLink size={15} />Open ticket</Link>
          <ArchiveButton />
        </div>
        {problem && <div className="problem" role="alert"><span>{problem.text}</span><button type="button" className="link-btn" onClick={() => setProblem(null)}>Dismiss</button></div>}
        {notice && <div className="notice" role="status"><span>{notice}</span><button type="button" className="link-btn" onClick={() => setNotice(null)}>Dismiss</button></div>}
        <NextStep row={row} />
        <CustomerCard />
        <div className="tp-pair"><TouchesCard /><MessagesCard /></div>
      </div>
    </TicketContext.Provider>
  );
}

function ArchiveButton() {
  const { detail, lookups, save, saving } = useTicketContext();
  const id = lookups?.statuses.find((s) => s.systemKey === "lead_archived")?.id;
  if (!id || detail.ticket.statusId === id) return null;
  return <button type="button" className="btn outline danger-text" disabled={saving} onClick={async () => {
    if (await confirmBox(`Move #${detail.ticket.ticketNo} to Lead Archived? It comes back to To do if the customer writes.`, { title: "Archive lead", ok: "Archive", danger: true }))
      await save("PATCH", "", { statusId: id, expectedUpdatedAt: detail.ticket.updatedAt });
  }}>Archive</button>;
}

/**
 * A button for something that waits on Shippo, the PDF or the server: a spinner and what it's doing while it runs,
 * and no second click (owner, 2026-11-04).
 */
function WorkButton({ className, busyText, onRun, disabled, title, children }: {
  className: string; busyText: string; onRun: () => Promise<unknown>; disabled?: boolean; title?: string; children: React.ReactNode;
}) {
  const { saving } = useTicketContext();
  const [on, setOn] = useState(false);
  return (
    <button type="button" className={className} disabled={disabled || on || saving} title={title} aria-busy={on}
      onClick={() => { setOn(true); void onRun().finally(() => setOn(false)); }}>
      {on ? <><Loader2 size={15} className="spin" aria-hidden="true" />{busyText}</> : children}
    </button>
  );
}

/** Customer's shipping-labels switch is off: say so, and offer to turn it on. */
function LabelsOff() {
  const { detail, save } = useTicketContext();
  if (detail.customer?.allowShippingLabels !== false) return null;
  return (
    <div className="ns-block">
      <p><strong>Shipping labels are off for this customer,</strong> so no label is bought automatically for their leads. Buy label &amp; send still works.</p>
      <div className="btn-row wrap"><WorkButton className="btn outline" busyText="Saving…" onRun={() => save("POST", "/labels-on")}>Turn shipping labels on</WorkButton></div>
    </div>
  );
}

/** What the lead needs next, by its tags and status. */
function NextStep({ row }: { row: LeadRow }) {
  const { detail, save, saving } = useTicketContext();
  const intake = detail.labels.filter((l) => l.toShop && !l.voidedAt).sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0];
  const labelFailed = detail.messages.find((m) => m.isSystem && /^Label 1 could not be bought/.test(m.body))?.body.replace(/^Label 1 could not be bought:\s*/, "");
  const tags = row.tags;
  const mailer = row.mailer;
  const blocks: React.ReactNode[] = [];

  if (tags.includes("reply")) blocks.push(
    <div key="reply" className="ns-block">
      <p><strong>New message from the customer.</strong> Reply in Touches (text) or Messages (email). It leaves To do once marked read.</p>
      <div className="btn-row wrap"><button type="button" className="btn primary" disabled={saving} onClick={() => void save("POST", "/mark-read")}>Mark read</button>
        {intake && (row.statusKey === "label_sent" || row.statusKey === "label_sent_old") && <ResendLabel />}</div>
    </div>);

  if (tags.includes("address")) blocks.push(
    <div key="label" className="ns-block">
      <p><strong>Check the address.</strong>{" "}
        {labelFailed ? <>The label couldn't be bought: {labelFailed}. </> : null}
        Fix the address under Customer Information if needed (it's checked with Shippo), then buy and send the label.</p>
      <div className="btn-row wrap">
        <WorkButton className="btn green" busyText="Talking to Shippo…" onRun={() => save("POST", "/send-label")}>Buy label &amp; send</WorkButton>
      </div>
    </div>);

  if (tags.includes("insurance")) blocks.push(<InsuranceStep key="ins" />);

  if (tags.includes("mail-label") || tags.includes("mail-box") || row.statusKey === "mailer_outgoing" || row.statusKey === "mailer_arrived") {
    blocks.push(<MailerStep key="mailer" row={row} hasLabel={!!intake} />);
  }

  if (!blocks.length && (row.statusKey === "label_sent" || row.statusKey === "label_sent_old")) blocks.push(
    <div key="follow" className="ns-block">
      <p><strong>Label sent {row.days} day{row.days === 1 ? "" : "s"} ago; no package scan yet.</strong>
        {row.days < 90 ? ` Moves to Lead Archived in ${90 - row.days} days if nothing arrives.` : ""}</p>
      <div className="btn-row wrap">
        <ResendLabel />
        <MailerStart />
      </div>
    </div>);

  if (!blocks.length) blocks.push(<p key="none" className="hint">Nothing to do here right now.</p>);

  return (
    <section className="next-step" aria-label="Next step">
      <div className="ns-head"><span className="ns-title">Next step</span>
        {tags.map((g) => <span key={g} className={`lead-pill ${g}`}>{TAG_LABEL[g]}</span>)}
        <span className="spacer" />
        {intake?.labelUrl && <a className="link-btn" href={intake.labelUrl} target="_blank" rel="noreferrer">View label</a>}
        {intake?.trackingNumber && trackingUrl(intake.carrier, intake.trackingNumber) && <a className="link-btn" href={trackingUrl(intake.carrier, intake.trackingNumber)!} target="_blank" rel="noreferrer">Track package</a>}
      </div>
      {blocks}
      <LabelsOff />
    </section>
  );
}

function ResendLabel() {
  const { save, saving } = useTicketContext();
  return <button type="button" className="btn outline" disabled={saving} onClick={async () => {
    if (await confirmBox("Email the label link again, and text it if the customer allows texts?", { title: "Resend label link", ok: "Resend" })) await save("POST", "/resend-label");
  }}>Resend label link</button>;
}

function MailerStart() {
  const { save, saving } = useTicketContext();
  return <>
    <WorkButton className="btn outline" busyText="Starting mailer…" onRun={() => save("POST", "/mailer", { kind: "label" })}>Mail a label instead</WorkButton>
    <WorkButton className="btn outline" busyText="Starting mailer…" onRun={() => save("POST", "/mailer", { kind: "box" })}>Mail a box</WorkButton>
  </>;
}

function InsuranceStep() {
  const { detail, lookups, save, saving } = useTicketContext();
  const t = detail.ticket;
  const [value, setValue] = useState(t.insuranceAmount ? String(Number(t.insuranceAmount)) : "");
  return (
    <div className="ns-block">
      <p><strong>Shipping protection of {money(t.insuranceAmount ?? 0)} is over the {money(2500)} limit for a label,</strong> so no label was made.
        Lower the declared value with the customer and send a label, or mail them a box.</p>
      <div className="btn-row wrap ns-inline">
        <label>Declared value <input type="number" min={0} step={1} value={value} onChange={(e) => setValue(e.target.value)} /></label>
        <button type="button" className="btn outline" disabled={saving || value === String(Number(t.insuranceAmount ?? 0))}
          onClick={() => void save("PATCH", "", { insuranceAmount: value === "" ? null : value, expectedUpdatedAt: t.updatedAt })}>Save</button>
        <button type="button" className="btn green" disabled={saving || Number(t.insuranceAmount ?? 0) > 2500}
          title={Number(t.insuranceAmount ?? 0) > 2500 ? "Lower the declared value to $2,500 or less first" : undefined}
          onClick={() => void save("POST", "/send-label")}>{saving ? <><Loader2 size={15} className="spin" aria-hidden="true" />Talking to Shippo…</> : "Buy label & send"}</button>
        <WorkButton className="btn outline" busyText="Starting mailer…" onRun={() => save("POST", "/mailer", { kind: "box" })}>Mail a box</WorkButton>
      </div>
      {lookups && <p className="hint">The first {money(lookups.pricing.freeInsuranceAmount)} is free; above that {Math.round(lookups.pricing.insuranceRate * 100)}% is added to the estimate.</p>}
    </div>
  );
}

const OUT_SERVICES: [string, string][] = [["usps_ground_advantage", "USPS Ground Advantage"], ["ups_ground", "UPS Ground"]];
const RETURN_SERVICES: [string, string][] = [["usps_ground_advantage", "USPS Ground Advantage"], ["ups_ground", "UPS Ground"], ["ups_second_day_air", "UPS 2nd Day Air"]];

/** Opens a base64 PDF in the tab opened on the click (so the browser doesn't block it); returns its address for a download link. */
function showPdf(tab: Window | null, base64: string): string {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  try { if (tab && !tab.closed) tab.location.href = url; } catch { /* the tab went away: the download link still works */ }
  return url;
}

/**
 * The mailer, as the WRP Label Maker does it (owner, 2026-11-04). Box: buy the outgoing label (the customer's
 * pay-on-use return label is reused unless they want a different one), print box label + return label + packing
 * slip on the 4×6 printer; it's on its way at once. Envelope: print return label + packing slip, mail with a stamp,
 * mark sent; it counts as arrived 7 days later. Steps: Requested → Printed → Mailed → On its way → Arrived.
 */
function MailerStep({ row, hasLabel }: { row: LeadRow; hasLabel: boolean }) {
  const { config, detail, save, saving, reload } = useTicketContext();
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const m = row.mailer;
  const box = m ? m.kind === "box" : row.statusKey === "mail_box";
  const intake = detail.labels.filter((l) => l.toShop && !l.voidedAt).sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0];
  const [outSvc, setOutSvc] = useState("usps_ground_advantage");
  const [retSvc, setRetSvc] = useState(intake ? "reuse" : "usps_ground_advantage");
  const [signature, setSignature] = useState(false);
  const [printed, setPrinted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [addressProblem, setAddressProblem] = useState<string | null>(null);
  const [shipAnyway, setShipAnyway] = useState(false);

  // The print-out is fetched on its own, so the notice from buying the labels stays on screen.
  const print = async (tab: Window | null) => {
    setErr(null);
    try {
      const d = await apiSend<{ mailerPdf?: string }>(config, token, "POST", `/tickets/${row.ticketNo}/mailer-pdf`, {});
      if (d.mailerPdf) { setPdfUrl(showPdf(tab, d.mailerPdf)); setPrinted(true); } else tab?.close();
    } catch (e) { tab?.close(); setErr((e as Error).message); }
  };
  const buyAndPrint = async () => {
    const tab = window.open("", "_blank");
    const d = await save("POST", "/mailer-prepare", { outboundService: outSvc, returnService: retSvc, signature: retSvc !== "reuse" && signature, skipValidation: shipAnyway });
    if (!d) { tab?.close(); return; }
    if (d.mailerAddressProblem) { tab?.close(); setAddressProblem(d.mailerAddressProblem); return; }
    setAddressProblem(null);
    await print(tab);
  };
  const act = async (path: string) => {
    setBusy(true); setErr(null);
    try { await apiSend(config, token, "POST", path, {}); reload(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const stage = !m ? 0 : m.state === "requested" ? (printed ? 2 : 1) : m.state === "sent" ? 3 : 5;
  const arrivesOn = m?.sentAt && !box ? date(new Date(new Date(m.sentAt).getTime() + 7 * 86_400_000).toISOString()) : null;
  const steps = [
    { label: "Requested", sub: m ? `${date(m.requestedAt)} · ${m.byCustomer ? "customer" : "staff"}` : "" },
    { label: "Printed", sub: printed ? "4×6 PDF opened" : "" },
    { label: "Mailed", sub: m?.sentAt ? date(m.sentAt) : "" },
    { label: "On its way", sub: box ? (m?.trackingNumber ?? "") : arrivesOn ? `arrives ${arrivesOn}` : "" },
    { label: "Arrived", sub: m?.deliveredAt ? date(m.deliveredAt) : "" },
  ];
  const track = m?.trackingNumber ? trackingUrl(carrierFor(m.trackingNumber), m.trackingNumber) : null;
  const what = box ? "box with an outgoing label, the return label and the packing slip" : "return label and packing slip in a stamped envelope";

  return (
    <div className="ns-block">
      <p><strong>{!m ? `Mail a ${what}.`
        : m.state === "requested" ? `Mail a ${what} (asked for ${date(m.requestedAt)} by the ${m.byCustomer ? "customer" : "staff"}).`
        : m.state === "sent" ? (box ? `Box mailed ${date(m.sentAt)}. It moves to Mailer Arrived when the carrier scans it delivered.`
          : `Envelope mailed ${date(m.sentAt)}. It moves to Mailer Arrived on ${arrivesOn}.`)
        : `${box ? "Box" : "Envelope"} arrived ${date(m.deliveredAt)}; the customer was emailed and texted. Waiting for the watch.`}</strong></p>
      <ol className="mailer-steps">
        {steps.map((s, i) => (
          <li key={s.label} className={i < stage ? "done" : i === stage ? "now" : ""}><span className="bar" /><span className="lbl">{s.label}</span><span className="sub">{s.sub}</span></li>
        ))}
      </ol>
      {!m && <div className="btn-row wrap"><WorkButton className="btn primary" busyText="Starting mailer…" onRun={() => save("POST", "/mailer", { kind: box ? "box" : "label" })}>Start mailer</WorkButton></div>}
      {m?.state === "requested" && (<>
        <div className="mailer-form">
          {box && <label>Box goes out by<select value={outSvc} onChange={(e) => setOutSvc(e.target.value)}>
            {OUT_SERVICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>}
          <label>Return label<select value={retSvc} onChange={(e) => setRetSvc(e.target.value)}>
            {intake && <option value="reuse">Use the current label ({intake.carrier}{intake.serviceLevel ? ` ${intake.serviceLevel}` : ""}{intake.signature ? ", signature" : ""})</option>}
            {RETURN_SERVICES.map(([v, l]) => <option key={v} value={v}>New: {l}</option>)}</select></label>
          {retSvc !== "reuse" && <label className="check"><input type="checkbox" checked={signature} onChange={(e) => setSignature(e.target.checked)} /> Signature required at WRP</label>}
        </div>
        {retSvc !== "reuse" && intake && <p className="hint">A new return label replaces the current one, which is voided in Shippo. Return labels are pay-on-use: billed only if the customer ships it.</p>}
        {addressProblem && (
          <div className="warn-box"><p className="warn">{addressProblem}</p>
            <label className="check"><input type="checkbox" checked={shipAnyway} onChange={(e) => setShipAnyway(e.target.checked)} /> Ship anyway</label></div>
        )}
        <div className="btn-row wrap">
          {box || retSvc !== "reuse"
            ? <WorkButton className="btn green" busyText="Talking to Shippo…" onRun={buyAndPrint}><Printer size={15} />{box ? "Buy labels & print" : "Buy return label & print"}</WorkButton>
            : <WorkButton className="btn primary" busyText="Building the PDF…" disabled={!hasLabel} onRun={() => print(window.open("", "_blank"))}><Printer size={15} />Print label &amp; slip</WorkButton>}
          {!box && <WorkButton className="btn outline" busyText="Saving…" disabled={busy || !hasLabel} onRun={() => act(`/mailers/${m.id}/sent`)}>Mark sent</WorkButton>}
          <button type="button" className="link-btn danger-text" disabled={busy} onClick={async () => {
            if (await confirmBox("Cancel this mailer?", { ok: "Cancel mailer", cancel: "Keep it", danger: true })) await act(`/mailers/${m.id}/cancel`);
          }}>Cancel mailer</button>
        </div>
        <p className="hint">Prints 4×6 pages for the label printer: {box ? "the box label, then " : ""}the return label, then the packing slip.</p>
      </>)}
      {m && m.state !== "requested" && (
        <div className="btn-row wrap">
          <WorkButton className="btn outline" busyText="Building the PDF…" onRun={() => print(window.open("", "_blank"))}><Printer size={15} />Print again</WorkButton>
          {track && <a className="btn outline" href={track} target="_blank" rel="noreferrer">Track box</a>}
          {m.state === "sent" && <button type="button" className="btn outline" disabled={busy} onClick={async () => {
            if (await confirmBox("Mark this mailer arrived now? The customer is emailed and texted that it should have arrived.", { title: "Mailer arrived", ok: "Mark arrived" })) await act(`/mailers/${m.id}/delivered`);
          }}>Mark arrived now</button>}
          {m.state === "delivered" && <ResendLabel />}
        </div>
      )}
      {pdfUrl && <p className="hint">The PDF opened in a new tab. If it didn't, <a href={pdfUrl} download={`WRP-ticket-${row.ticketNo}-mailer.pdf`}>download it here</a>.</p>}
      {err && <p className="warn">{err}</p>}
    </div>
  );
}
